import { useEffect } from "react";
import type * as type from "@vTypes";
import type { useRosterResources } from "@/hooks/useRosterMetaresource";
import type { NurseResponse } from "@/types/nurse-type";
import {
  secondsToTimeString,
  type FullNurseSchemaWithPreceptor,
} from "@utils/rosterCreate-util";
import { sortNurseListByRoleThenSequence } from "@utils/common-util";

type RosterResourceData = NonNullable<
  ReturnType<typeof useRosterResources>["data"]
>;
type FullNurseSchemaAddPreceptor = FullNurseSchemaWithPreceptor[];

type UseRosterCreateResourceSyncParams = {
  readonly data: RosterResourceData | null | undefined;
  readonly setFullNurseSchema: (value: NurseResponse[]) => void;
  readonly setFullNurseSchemaAddPreceptor: (
    value: FullNurseSchemaAddPreceptor
  ) => void;
  readonly setAvailableShifts: (value: type.availableShifts[]) => void;
  readonly setCurrentConfigVer: (value: number | null) => void;
  readonly setCurrentShiftManage: (
    value: type.ShiftManageObject[] | undefined
  ) => void;
  readonly setFullWanted: (value: type.WantedAll[]) => void;
  readonly setIsLoadedPage: (value: boolean) => void;
};

export function useRosterCreateResourceSync({
  data,
  setFullNurseSchema,
  setFullNurseSchemaAddPreceptor,
  setAvailableShifts,
  setCurrentConfigVer,
  setCurrentShiftManage,
  setFullWanted,
  setIsLoadedPage,
}: UseRosterCreateResourceSyncParams) {
  useEffect(() => {
    if (!data) return;

    if (data.nurses) {
      setFullNurseSchema(sortNurseListByRoleThenSequence([...data.nurses]));
    }

    if (data.fullNurseSchemaAddPreceptor) {
      setFullNurseSchemaAddPreceptor(data.fullNurseSchemaAddPreceptor);
    }

    if (data.shifts) {
      const updatedShifts = [...data.shifts]
        .sort((a, b) => a.sequence - b.sequence)
        .map<type.availableShifts>((shift) => ({
          ...shift,
          start_time: secondsToTimeString(shift.start_time),
          end_time: secondsToTimeString(shift.end_time),
          allday: Boolean(shift.allday),
          auto_schedule: Boolean(shift.auto_schedule),
        }));

      setAvailableShifts(updatedShifts);
    }

    if (data.configs) {
      setCurrentConfigVer(data.configs.config_id || null);
    }

    if (data.shiftManager) {
      setCurrentShiftManage(data.shiftManager);
    }

    if (data.wantedList) {
      setFullWanted(data.wantedList);
    }

    setIsLoadedPage(true);
  }, [
    data,
    setFullNurseSchema,
    setFullNurseSchemaAddPreceptor,
    setAvailableShifts,
    setCurrentConfigVer,
    setCurrentShiftManage,
    setFullWanted,
    setIsLoadedPage,
  ]);
}
